import { Fragment, type JSX } from "react";
import {
  Image,
  Text,
  type LinkField,
} from "@sitecore-content-sdk/nextjs";
import { SitecoreLink as Link } from "../../atoms/SitecoreLink";
import { CopyrightProps } from "components/Copyright/copyright.props";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";

export const Default = (props: CopyrightProps): JSX.Element => {
  console.log("---->[Copyright] Component", props.fields);

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Copyright" />;
  }

  const year = new Date().getFullYear().toString();

  const styles = `${params?.Styles ?? ""}`.trimEnd();
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const legalLinks: LinkField[] = [
    fields.SafetyDataSheetLink,
    fields.PrivacyPolicyLink,
    fields.TermsOfUseLink,
  ].filter((link): link is LinkField => !!link?.value?.href);

  const logoSrc = fields.EcolabLogo?.value?.src;
  const logoAlt =
    typeof fields.EcolabLogo?.value?.alt === "string"
      ? fields.EcolabLogo.value.alt
      : "Ecolab";
  const hasLogo = !!logoSrc;

  const linkClass =
    "text-white font-normal border-b border-transparent hover:border-white focus-visible:border-white";

  return (
    <div
      className={`copyright-component ${hiddenBlockStyles ?? ""} -mt-px rounded-none `}
      data-role="copyright-component"
      style={{ backgroundColor: "#006BD3" }}
    >
      <div className="component-content">
        <div
          className={`
            px-6 lg:px-8
            py-8 lg:py-6
            text-white
            text-[13px] leading-[18px]
            lg:text-[14px] lg:leading-[18px]
            font-normal
            ${styles}
          `}
        >
          {/* ================= MOBILE ================= */}
          <div className="flex flex-col gap-4 lg:hidden">
            {legalLinks.length > 0 && (
              <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 text-center">
                {legalLinks.map((link, idx) => (
                  <Fragment key={link.value?.href ?? `legal-${idx}`}>
                    {idx > 0 && <span aria-hidden="true"> | </span>}
                    <Link field={link} className={linkClass} />
                  </Fragment>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-3">
              <div className="whitespace-nowrap">
                {fields.CopyrightText && (
                  <Text tag="span" field={fields.CopyrightText} />
                )}
                <span> {year}</span>
              </div>

              {hasLogo && fields.EcolabLogo && (
                <Image
                  field={fields.EcolabLogo}
                  className="h-5 w-auto"
                  alt={logoAlt}
                />
              )}
            </div>
          </div>

          {/* ================= DESKTOP ================= */}
          <div className="hidden lg:flex lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="whitespace-nowrap">
                {fields.CopyrightText && (
                  <Text tag="span" field={fields.CopyrightText} />
                )}
                <span> {year}</span>
              </span>

              {legalLinks.map((link, idx) => (
                <Fragment key={link.value?.href ?? `legal-${idx}`}>
                  <span aria-hidden="true"> | </span>
                  <Link field={link} className={linkClass} />
                </Fragment>
              ))}
            </div>

            {hasLogo && fields.EcolabLogo && (
              <Image
                field={fields.EcolabLogo}
                className="h-6 w-auto"
                alt={logoAlt}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
